/**
 * Общие константы контура: регионы, типы миров, лимиты, коды закрытия сокета.
 * Экономические константы живут в packages/sim/src/economy и сюда не попадают.
 */

/**
 * Регионы мира - ИГРОВАЯ сущность (01 §8), а не список платформенных хинтов.
 * Их семь, они видны игроку в лобби и не обязаны совпадать с тем, что умеет
 * Cloudflare: apac-ne и apac-se существуют для матчмейкинга, у платформы для
 * обоих один хинт, а платформенные sam и afr игре не нужны.
 */
export const REGIONS = ['wnam', 'enam', 'weur', 'eeur', 'apac-ne', 'apac-se', 'oc'] as const;
export type Region = (typeof REGIONS)[number];

/** Полный список значений locationHint, которые принимает Durable Objects. */
export type DoLocationHint = 'wnam' | 'enam' | 'sam' | 'weur' | 'eeur' | 'apac' | 'oc' | 'afr';

/**
 * Проекция игрового региона на хинт платформы.
 * Тип значения сужен намеренно: Record<Region, string> пропустил бы опечатку
 * вроде 'wnma' молча, а мир уехал бы не в тот дата-центр.
 *
 * locationHint - это ПОДСКАЗКА, а не гарантия: при эвакуации Durable Object
 * окажется в другом месте. Обещать игроку "мир в вашем регионе" как факт нельзя.
 */
export const DO_LOCATION_HINT: Record<Region, DoLocationHint> = {
  wnam: 'wnam',
  enam: 'enam',
  weur: 'weur',
  eeur: 'eeur',
  'apac-ne': 'apac',
  'apac-se': 'apac',
  oc: 'oc',
};

export const WORLD_TYPES = ['BLITZ', 'STANDARD', 'ENDLESS', 'PRIVATE'] as const;
export type WorldType = (typeof WORLD_TYPES)[number];

/**
 * INITIALIZING - мир заведён в реестре, но его Durable Object ещё не подтвердил
 * инициализацию. Такие записи не показываются в лобби и подбираются кроном:
 * без этого состояния сбой между записью в D1 и init оставлял бы в лобби
 * мир-фантом, у которого нет ни даты, ни тика.
 */
export const WORLD_STATUSES = ['INITIALIZING', 'RUNNING', 'FINALE', 'FINISHED'] as const;
export type WorldStatus = (typeof WORLD_STATUSES)[number];

/** Скорость мира фиксируется при создании и не меняется (инвариант 6). */
export const TICK_MINUTES: Record<WorldType, number> = {
  BLITZ: 5,
  STANDARD: 20,
  ENDLESS: 20,
  PRIVATE: 20,
};

export const WORLD_YEAR_MIN = 1950;
export const WORLD_YEAR_MAX = 2030;
export const MAX_PLAYERS_DEFAULT = 60;

/** Версия REST-контракта, отдаётся в /v1/meta. */
export const API_VERSION = 1;
/** Версия payload дня в WebSocket-пуше world.day. */
export const DAY_PAYLOAD_VERSION = 1;

/**
 * Коды закрытия WebSocket. Диапазон 4000-4999 отдан приложению.
 * Клиент по коду решает, переподключаться или нет.
 */
export const WS_CLOSE = {
  /** Нет валидной сессии. Переподключение без нового входа бессмысленно. */
  UNAUTHORIZED: 4401,
  /** Мир не найден или архивирован. */
  WORLD_NOT_FOUND: 4404,
  /** Клиент превысил лимит команд. Переподключаться с задержкой. */
  RATE_LIMITED: 4429,
  /** Мир завершён, сокет больше не нужен. */
  WORLD_FINISHED: 4410,
} as const;

/** Автоответ на heartbeat: сокет спит в hibernation и не будит DO. */
export const WS_PING = 'ping';
export const WS_PONG = 'pong';
export const WS_PING_INTERVAL_MS = 25_000;

/** Reconnect с экспоненциальным backoff и джиттером (PHASE_0 §5.1). */
export const WS_RECONNECT_MIN_MS = 1_000;
export const WS_RECONNECT_MAX_MS = 30_000;
/** Доля джиттера от задержки. */
export const WS_RECONNECT_JITTER = 0.3;

/**
 * Задержка перед попыткой переподключения.
 *
 * Джиттер обязателен: без него все клиенты мира вернутся одновременно и добьют
 * объект, который только что упал. Функция чистая - случайность приходит
 * аргументом, поэтому поведение проверяемо тестом, а не наблюдением.
 *
 * @param attempt номер попытки, начиная с 1
 * @param random значение в [0, 1)
 */
export function reconnectDelayMs(attempt: number, random: number): number {
  if (attempt < 1) throw new RangeError(`attempt must be >= 1, got ${attempt}`);
  const base = Math.min(WS_RECONNECT_MIN_MS * 2 ** (attempt - 1), WS_RECONNECT_MAX_MS);
  return base + random * base * WS_RECONNECT_JITTER;
}

/**
 * Кэш списка миров.
 *
 * KV не принимает expirationTtl меньше 60 секунд - put с 30 бросит ошибку и
 * уронит эндпоинт лобби целиком. Поэтому логическая свежесть (30 с из спеки)
 * проверяется по метке времени внутри значения, а TTL самого ключа держим
 * минимально допустимым.
 *
 * Плюс KV согласован в конечном счёте: между локациями значение расходится, и
 * фактический возраст данных лобби может доходить до полутора минут.
 */
export const LOBBY_FRESH_S = 30;
export const KV_MIN_TTL_S = 60;

/**
 * Лимиты контракта, которые клиент обязан знать ДО отправки, иначе он узнаёт
 * их по 4xx с прода.
 */
export const WS_MAX_MESSAGE_BYTES = 32_768;
export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 20;
export const WORLD_NAME_MAX_LENGTH = 64;

export const DEFAULT_LOCALE = 'en';
